import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./styles.module.css";
import Input from "../Input";

const defaultGetOptionLabel = (option) =>
  option?.label ?? option?.name ?? String(option);
const defaultGetOptionValue = (option) =>
  option?.id ?? option?.value ?? option;

const SearchableSelect = ({
  options = [],
  value,
  onChange,
  onSelect,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  label,
  placeholder = "Select...",
  disabled,
  id,
  className,
  "aria-label": ariaLabel,
  // Async mode: fetch options via onSearch (debounced)
  async: isAsync = false,
  onSearch,
  minSearchLength = 0,
  searchDebounceMs = 1000,
  isLoading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const selectedOption = options.find(
    (opt) => String(getOptionValue(opt)) === String(value)
  );
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : "";

  const filteredOptions = useMemo(() => {
    if (isAsync) return options;
    if (!search.trim()) return options;
    const term = search.toLowerCase();
    return options.filter((opt) =>
      getOptionLabel(opt).toLowerCase().includes(term)
    );
  }, [options, search, getOptionLabel, isAsync]);

  useEffect(() => {
    if (!isAsync || !open) return;
    const t = setTimeout(() => {
      const fn = onSearchRef.current;
      if (typeof fn !== "function") return;
      if (search.length < minSearchLength) {
        fn("");
        return;
      }
      fn(search);
    }, searchDebounceMs);
    return () => clearTimeout(t);
  }, [isAsync, open, search, searchDebounceMs, minSearchLength]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setFocusedIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    if (!open || focusedIndex < 0 || !panelRef.current) return;
    const opt = filteredOptions[focusedIndex];
    if (!opt) return;
    const el = panelRef.current.querySelector(
      `[id="searchable-option-${getOptionValue(opt)}"]`
    );
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [open, focusedIndex, filteredOptions, getOptionValue]);

  const handleInputChange = (val) => {
    setSearch(val);
    setOpen(true);
    setFocusedIndex(-1);
    if (!val && value != null && onChange) onChange(null);
  };

  const handleFocus = () => {
    setOpen(true);
    setSearch(displayValue);
  };

  const handleSelect = (option) => {
    const val = getOptionValue(option);
    if (onChange) onChange(val);
    if (onSelect) onSelect(option);
    setOpen(false);
    setSearch("");
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setSearch(displayValue);
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((i) =>
        i < filteredOptions.length - 1 ? i + 1 : 0
      );
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((i) =>
        i > 0 ? i - 1 : filteredOptions.length - 1
      );
      return;
    }
    if (e.key === "Enter" && focusedIndex >= 0 && filteredOptions[focusedIndex]) {
      e.preventDefault();
      handleSelect(filteredOptions[focusedIndex]);
    }
  };

  const inputDisplay = open ? search : displayValue;

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${className || ""}`.trim()}
    >
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <div className={styles.inputWrap}>
        <Input
          id={id}
          type="text"
          value={inputDisplay}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel || label || (isAsync ? "Search" : "Searchable select")}
        />
        {open && (
          <div
            ref={panelRef}
            className={styles.panel}
            role="listbox"
            aria-activedescendant={
              focusedIndex >= 0 && filteredOptions[focusedIndex]
                ? `searchable-option-${getOptionValue(filteredOptions[focusedIndex])}`
                : undefined
            }
          >
            {isAsync && isLoading ? (
              <div className={styles.loading}>Loading…</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const optValue = getOptionValue(option);
                const isSelected =
                  value != null && String(optValue) === String(value);
                return (
                  <button
                    key={optValue}
                    type="button"
                    role="option"
                    id={`searchable-option-${optValue}`}
                    aria-selected={isSelected}
                    className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`.trim()}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setFocusedIndex(index)}
                  >
                    {getOptionLabel(option)}
                  </button>
                );
              })
            ) : (
              <div className={styles.noOptions}>
                {isAsync && search.length < minSearchLength && minSearchLength > 0
                  ? `Type at least ${minSearchLength} character(s) to search`
                  : isAsync
                    ? "No results"
                    : "No options"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

SearchableSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  async: PropTypes.bool,
  onSearch: PropTypes.func,
  minSearchLength: PropTypes.number,
  searchDebounceMs: PropTypes.number,
  isLoading: PropTypes.bool,
};

export default SearchableSelect;
